/**
 * Admin File Service
 * Handles file uploads and management for administrators
 */
import axios from 'axios';
import { getConfig } from '@/config/Constants';
import type {
  CourseFile,
  CourseFileListResponse,
  FileUploadUrlRequest,
  FileUploadUrlResponse,
  FileUploadConfirmRequest,
  FileLevelType,
  FileValidationResult,
  FileUploadProgress,
} from '@/types/courseFile.types';

// Allowed extensions and max size
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv',
  '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.zip', '.rar', '.py', '.js', '.ts', '.json', '.html', '.css',
  '.txt', '.md'
];
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB

class AdminFileService {
  private getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Validate a file before upload
   */
  validateFile(file: File): FileValidationResult {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size (${this.formatFileSize(file.size)}) exceeds maximum of 1GB`,
      };
    }

    // Check extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return {
        valid: false,
        error: `File type "${ext}" is not allowed. Supported: ${ALLOWED_EXTENSIONS.join(', ')}`,
      };
    }

    return { valid: true };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Upload file using presigned URL flow
   */
  async uploadFile(
    data: {
      courseId: string;
      levelType: FileLevelType;
      levelId: string;
      title: string;
      description?: string;
      file: File;
    },
    onProgress?: (progress: FileUploadProgress) => void,
  ): Promise<CourseFile> {
    const apiUrl = getConfig().apiUrl;

    try {
      // Step 1: Validate file
      onProgress?.({
        status: 'preparing',
        progress: 0,
        message: 'Validating file...',
      });

      const validation = this.validateFile(data.file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Step 2: Get presigned URL
      onProgress?.({
        status: 'preparing',
        progress: 10,
        message: 'Requesting upload URL...',
      });

      const uploadUrlRequest: FileUploadUrlRequest = {
        course_id: data.courseId,
        level_type: data.levelType,
        level_id: data.levelId,
        filename: data.file.name,
        content_type: data.file.type || 'application/octet-stream',
        file_size: data.file.size,
      };

      const urlResponse = await axios.post<FileUploadUrlResponse>(
        `${apiUrl}/admin/files/generate-upload-url`,
        uploadUrlRequest,
        { headers: this.getAuthHeaders() }
      );

      // Step 3: Upload to S3
      onProgress?.({
        status: 'uploading',
        progress: 20,
        message: 'Uploading file to storage...',
      });

      await axios.put(urlResponse.data.upload_url, data.file, {
        headers: {
          'Content-Type': data.file.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 60) / progressEvent.total) + 20
            : 50;
          onProgress?.({
            status: 'uploading',
            progress: percentCompleted,
            message: `Uploading... ${percentCompleted - 20}%`,
          });
        },
      });

      // Step 4: Confirm upload
      onProgress?.({
        status: 'confirming',
        progress: 85,
        message: 'Confirming upload...',
      });

      const confirmRequest: FileUploadConfirmRequest = {
        course_id: data.courseId,
        level_type: data.levelType,
        level_id: data.levelId,
        s3_key: urlResponse.data.s3_key,
        title: data.title,
        description: data.description,
        original_filename: data.file.name,
        file_size: data.file.size,
        content_type: data.file.type || 'application/octet-stream',
      };

      const confirmResponse = await axios.post<CourseFile>(
        `${apiUrl}/admin/files/confirm-upload`,
        confirmRequest,
        { headers: this.getAuthHeaders() }
      );

      onProgress?.({
        status: 'complete',
        progress: 100,
        message: 'Upload complete!',
      });

      return confirmResponse.data;

    } catch (error: any) {
      onProgress?.({
        status: 'error',
        progress: 0,
        message: error.response?.data?.detail || error.message || 'Upload failed',
      });
      throw error;
    }
  }

  /**
   * List all files
   */
  async listFiles(courseId?: string): Promise<CourseFileListResponse> {
    const apiUrl = getConfig().apiUrl;
    const params = courseId ? { course_id: courseId } : {};
    const response = await axios.get<CourseFileListResponse>(
      `${apiUrl}/admin/files/list`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
    return response.data;
  }

  /**
   * Delete a file
   */
  async deleteFile(fileId: string): Promise<{ success: boolean; message: string }> {
    const apiUrl = getConfig().apiUrl;
    const response = await axios.delete(
      `${apiUrl}/admin/files/${fileId}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  /**
   * Update file metadata
   */
  async updateFile(
    fileId: string,
    update: { title?: string; description?: string }
  ): Promise<CourseFile> {
    const apiUrl = getConfig().apiUrl;
    const response = await axios.patch<CourseFile>(
      `${apiUrl}/admin/files/${fileId}`,
      update,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export const adminFileService = new AdminFileService();
export type { CourseFile, CourseFileListResponse, FileLevelType };
